import React, { useEffect, useState } from 'react'
import { X, Smartphone, RefreshCw } from 'lucide-react'
import { useWhatsApp } from '../../hooks/useWhatsApp'

interface QRCodeModalProps {
  isOpen: boolean
  onClose: () => void
  botId: number
  botName: string
}

const QRCodeModal: React.FC<QRCodeModalProps> = ({ isOpen, onClose, botId, botName }) => {
  const { status, qrCode, isLoading, connect, checkStatus } = useWhatsApp(botId)
  const [refreshInterval, setRefreshInterval] = useState<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (isOpen && botId) {
      connect()
    }
  }, [isOpen, botId, connect])

  useEffect(() => {
    if (isOpen && status === 'authenticating') {
      const interval = setInterval(() => {
        checkStatus()
      }, 3000)
      setRefreshInterval(interval)
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval)
        setRefreshInterval(null)
      }
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }
    }
  }, [isOpen, status, checkStatus])

  useEffect(() => {
    if (status === 'connected') {
      onClose()
    }
  }, [status, onClose])

  if (!isOpen) return null

  const getStatusMessage = () => {
    switch (status) {
      case 'disconnected':
        return 'Iniciando conexão...'
      case 'authenticating':
        return 'Aguardando escaneamento do QR Code...'
      case 'connected':
        return 'Conectado com sucesso!'
      case 'failed':
        return 'Falha na conexão. Tente novamente.'
      default:
        return 'Preparando QR Code...'
    }
  }

  const getStatusColor = () => {
    switch (status) {
      case 'connected':
        return 'text-green-600'
      case 'authenticating':
        return 'text-blue-600'
      case 'failed':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <Smartphone className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Conectar WhatsApp</h2>
              <p className="text-sm text-gray-600">{botName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          {isLoading ? (
            <div className="py-12">
              <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
              <p className="text-gray-600">Preparando QR Code...</p>
            </div>
          ) : status === 'authenticating' && qrCode ? (
            <div>
              <div className="mb-4">
                <img
                  src={`data:image/png;base64,${qrCode}`}
                  alt="QR Code"
                  className="w-64 h-64 mx-auto border rounded-lg"
                />
              </div>
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Escaneie o QR Code
                </h3>
                <p className="text-sm text-gray-600">
                  Abra o WhatsApp no seu celular e escaneie este código
                </p>
                <div className="flex items-center justify-center space-x-2 text-sm text-blue-600">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Aguardando conexão...</span>
                </div>
              </div>
            </div>
          ) : status === 'connected' ? (
            <div className="py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-green-600 mb-2">
                Conectado com sucesso!
              </h3>
              <p className="text-sm text-gray-600">
                Seu bot está pronto para receber mensagens
              </p>
            </div>
          ) : status === 'failed' ? (
            <div className="py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-red-600 mb-2">
                Falha na conexão
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Não foi possível conectar ao WhatsApp
              </p>
              <button
                onClick={() => connect()}
                className="btn btn-primary btn-sm"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Tentar Novamente
              </button>
            </div>
          ) : (
            <div className="py-12">
              <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
              <p className="text-gray-600">{getStatusMessage()}</p>
            </div>
          )}

          {/* Status */}
          <div className="mt-6 pt-4 border-t">
            <div className={`text-sm font-medium ${getStatusColor()}`}>
              {getStatusMessage()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-lg">
          <div className="flex justify-between items-center">
            <button
              onClick={onClose}
              className="btn btn-outline btn-sm"
            >
              Cancelar
            </button>
            {status === 'authenticating' && (
              <button
                onClick={() => connect()}
                className="btn btn-primary btn-sm"
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default QRCodeModal
